import React, {FC, useState} from "react";
import useSafeState from "../../utils/safe-state";
import {Trans} from "@lingui/macro";
import {Breadcrumb, Drawer, Tooltip, Table, Button} from "antd";
import {EyeOutlined, SafetyCertificateOutlined, FileOutlined, ExclamationCircleOutlined, FileExcelOutlined, LoadingOutlined, ReloadOutlined} from "@ant-design/icons/lib";
import DtoTable from "../../components/dto-table/dto-table";
import AutoCheckModel from "../../models/autochecks/auto-check-model";
import AutoCheckReportDrawer from "../../components/drawer/AutoCheckReportDrawer";
import ButtonWithIcon from "../../components/buttons/button-with-icon";
import {useAxios} from "../../utils/hooks";
import {openNotification} from "../../utils/openNotification";
import FileDownload from "js-file-download";

const AutoChecksScreen: FC = () => {

    const [trigger, setTrigger] = useSafeState(false);
    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const [recheckingIds, setRecheckingIds] = useSafeState<Array<number>>([]);
    const [gfwEufoReportDownloading, setGfwEufoReportDownloading] = useSafeState(false);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [errorDrawerVisible, setErrorDrawerVisible] = useState(false);
    const [reportDrawerVisible, setReportDrawerVisible] = useState(false);
    const [selectedProperties, setSelectedProperties] = useState<any>(null);
    const [selectedErrorMessage, setSelectedErrorMessage] = useState<any>(null);
    const [selectedType, setSelectedType] = useState<string>("");

    const handleViewProperties = (record: any) => {
        const properties = record.properties;
        if (properties) {
            setSelectedProperties(properties);
        } else {
            setSelectedProperties({});
        }
        setSelectedType(record.type || "Auto Check");
        setDrawerVisible(true);
    };

    const handleViewError = (record: any) => {
        const errorMessage = record.errorMessage;
        if (errorMessage) {
            setSelectedErrorMessage(errorMessage);
        } else {
            setSelectedErrorMessage({});
        }
        setSelectedType(record.type || "Auto Check");
        setErrorDrawerVisible(true);
    };

    const handleRecheck = (record: any) => {
        if (axiosInstance.current == null || recheckingIds.includes(record.id)) {
            return;
        }
        setRecheckingIds(prev => [...prev, record.id]);
        axiosInstance.current
            .post(`/resource/auto-checks/${record.id}/recheck`)
            .then(() => {
                openNotification('customSuccess', 'Re-check finished.');
            })
            .catch(function (error) {
                openNotification('error', error);
            })
            .finally(() => {
                setRecheckingIds(prev => prev.filter(id => id !== record.id));
                setTrigger(function (prev) { return !prev; });
            });
    };

    const handleDownloadGfwEufoReport = () => {
        if (axiosInstance.current == null) {
            return;
        }
        setGfwEufoReportDownloading(true);
        axiosInstance.current({
            url: '/resource/auto-checks/gfw-eufo-report',
            method: 'POST',
            responseType: 'blob'
        }).then((response) => {
            FileDownload(response.data, 'gfw-eufo-report-' + new Date().toISOString().split('.')[0] + '.xlsx');
        }).catch(function (error: any) {
            openNotification('error', error);
        }).finally(() => setGfwEufoReportDownloading(false));
    };

    const parseJsonToTableData = (properties: any) => {
        try {
            let parsed: any;
            if (typeof properties === 'string') {
                parsed = JSON.parse(properties);
            } else if (typeof properties === 'object' && properties !== null) {
                parsed = properties;
            } else {
                return [];
            }
            
            if (typeof parsed === 'object' && parsed !== null) {
                return Object.entries(parsed).map(([key, value], index) => ({
                    key: index,
                    property: key,
                    value: typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
                }));
            }
            return [];
        } catch (e) {
            console.log(e);
            return [];
        }
    };

    const tableColumns = [
        {
            title: <span style={{ fontWeight: '600' }}>Property</span>,
            dataIndex: 'property',
            key: 'property',
            width: '40%',
            render: (text: string) => (
                <span style={{ 
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    color: '#d63384',
                    fontWeight: '500',
                    fontSize: '13px'
                }}>
                    {text}
                </span>
            )
        },
        {
            title: <span style={{ fontWeight: '600' }}>Value</span>,
            dataIndex: 'value',
            key: 'value',
            width: '60%',
            render: (text: string) => (
                <span style={{ 
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    color: '#262626',
                    fontSize: '13px',
                    wordBreak: 'break-word'
                }}>
                    {text}
                </span>
            )
        }
    ];

    const action = (record: any) => {
        const isError = record.result === 'ERROR';
        const isRechecking = recheckingIds.includes(record.id);
        return (
            <>
                <Tooltip title={<Trans>View Properties</Trans>}>
                    <EyeOutlined
                        className={"ActionButtons"}
                        style={{ fontSize: '18px', cursor: 'pointer', color: '#1890ff', marginRight: '8px' }}
                        onClick={() => handleViewProperties(record)}
                    />
                </Tooltip>
                <Tooltip title={<Trans>Re-check</Trans>}>
                    {isRechecking ? (
                        <LoadingOutlined
                            className={"ActionButtons"}
                            style={{ fontSize: '18px', color: '#1890ff', marginRight: isError ? '8px' : '0' }}
                        />
                    ) : (
                        <ReloadOutlined
                            className={"ActionButtons"}
                            style={{ fontSize: '18px', cursor: 'pointer', color: '#1890ff', marginRight: isError ? '8px' : '0' }}
                            onClick={() => handleRecheck(record)}
                        />
                    )}
                </Tooltip>
                {isError && (
                    <Tooltip title={<Trans>View Error</Trans>}>
                        <ExclamationCircleOutlined 
                            className={"ActionButtons"} 
                            style={{ fontSize: '18px', cursor: 'pointer', color: '#ff4d4f' }}
                            onClick={() => handleViewError(record)}
                        />
                    </Tooltip>
                )}
            </>
        )
    };

    const actions = {
        width: 120,
        render: (text: string, record: any) => action(record),
        title: <Trans>Actions</Trans>,
        fixed: 'right',
        align: 'center'
    };

    return (
        <>
            <Breadcrumb style={{marginBottom: 10}}>
                <Breadcrumb.Item><Trans id={'Home'}/></Breadcrumb.Item>
                <Breadcrumb.Item><Trans id={'Auto Checks'}></Trans></Breadcrumb.Item>
            </Breadcrumb>
            
            <DtoTable
                model={AutoCheckModel}
                trigger={trigger}
                allowRefreshButton={true}
                allowExport={true}
                apiUrl="auto-checks"
                pageSize={100}
                scroll={{x: 1800, y: "65vh"}}
                action={actions}
                tableHeader={
                    <>
                        <Button
                            type="primary"
                            onClick={() => setReportDrawerVisible(true)}
                            style={{marginRight: 10}}
                        >
                            <ButtonWithIcon icon={<FileExcelOutlined />} text={<Trans>Make Report</Trans>} />
                        </Button>
                        <Button
                            loading={gfwEufoReportDownloading}
                            onClick={() => handleDownloadGfwEufoReport()}
                            style={{marginRight: 10}}
                        >
                            <ButtonWithIcon icon={<FileExcelOutlined />} text={<Trans>GFW + EUFO riport</Trans>} />
                        </Button>
                    </>
                }
            />

            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <SafetyCertificateOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>{selectedType}</span>
                        <span style={{ 
                            fontSize: '12px', 
                            color: '#8c8c8c',
                            fontWeight: '400',
                            marginLeft: '8px'
                        }}>
                            Properties
                        </span>
                    </div>
                }
                placement="right"
                onClose={() => setDrawerVisible(false)}
                visible={drawerVisible}
                width={800}
                bodyStyle={{ padding: '24px', backgroundColor: '#fafafa' }}
            >
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        padding: '16px 20px',
                        backgroundColor: '#f0f5ff',
                        borderBottom: '1px solid #d6e4ff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <FileOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                        <span style={{ 
                            fontWeight: '600', 
                            color: '#262626',
                            fontSize: '14px'
                        }}>
                            Configuration Properties
                        </span>
                    </div>
                    <div style={{ padding: '16px' }}>
                        <Table
                            columns={tableColumns}
                            dataSource={parseJsonToTableData(selectedProperties)}
                            pagination={false}
                            size="middle"
                            bordered
                        />
                    </div>
                </div>
            </Drawer>

            <Drawer
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <ExclamationCircleOutlined style={{ fontSize: '20px', color: '#ff4d4f' }} />
                        <span style={{ fontSize: '16px', fontWeight: '600' }}>{selectedType}</span>
                        <span style={{ 
                            fontSize: '12px', 
                            color: '#8c8c8c',
                            fontWeight: '400',
                            marginLeft: '8px'
                        }}>
                            Error Details
                        </span>
                    </div>
                }
                placement="right"
                onClose={() => setErrorDrawerVisible(false)}
                visible={errorDrawerVisible}
                width={800}
                bodyStyle={{ padding: '24px', backgroundColor: '#fafafa' }}
            >
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        padding: '16px 20px',
                        backgroundColor: '#fff1f0',
                        borderBottom: '1px solid #ffccc7',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                        <span style={{ 
                            fontWeight: '600', 
                            color: '#262626',
                            fontSize: '14px'
                        }}>
                            Error Information
                        </span>
                    </div>
                    <div style={{ padding: '16px' }}>
                        <Table
                            columns={tableColumns}
                            dataSource={parseJsonToTableData(selectedErrorMessage)}
                            pagination={false}
                            size="middle"
                            bordered
                        />
                    </div>
                </div>
            </Drawer>
            
            <AutoCheckReportDrawer
                visible={reportDrawerVisible}
                onClose={() => setReportDrawerVisible(false)}
            />
        </>
    )
}

export default AutoChecksScreen;
